import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE, timeout: 20000 });

// ---- Auth token attachment ----
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// ---- Backend status broadcast helpers ----
let _isOffline = false;
const emit = (name, detail) => {
  try { window.dispatchEvent(new CustomEvent(name, { detail })); } catch (_e) { /* ignore */ }
};
const setOnline = () => {
  if (_isOffline) {
    _isOffline = false;
    emit("api:online");
  }
};
const setOffline = (info) => {
  if (!_isOffline) {
    _isOffline = true;
    emit("api:offline", info);
  }
};
export const isBackendOffline = () => _isOffline;

// ---- Auto-retry on 5xx / network errors (2 retries: 1s then 2s) ----
const RETRY_CODES = new Set([502, 503, 504, 520, 521, 522, 523, 524]);
const MAX_RETRIES = 2;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

api.interceptors.response.use(
  (res) => { setOnline(); return res; },
  async (error) => {
    const cfg = error.config || {};
    // Do not retry mutations (safety) — only GET
    const method = (cfg.method || "get").toLowerCase();
    const status = error.response?.status;
    const isNetwork = !error.response; // no response = network / CORS / timeout
    const shouldRetry = method === "get" && (isNetwork || RETRY_CODES.has(status));

    cfg.__retryCount = cfg.__retryCount || 0;
    if (shouldRetry && cfg.__retryCount < MAX_RETRIES) {
      cfg.__retryCount += 1;
      await wait(1000 * cfg.__retryCount); // 1s, 2s
      try {
        const r = await api.request(cfg);
        setOnline();
        return r;
      } catch (e) {
        // fall through to offline signaling below
        error = e;
      }
    }

    // Final failure — broadcast offline for 5xx / network errors on GETs
    if (method === "get" && (isNetwork || (status && status >= 500))) {
      setOffline({ status: status || 0, url: cfg.url });
    }
    return Promise.reject(error);
  }
);

export default api;
